import React, {useCallback, useEffect, useMemo, useState} from 'react'
import Header from "../../ThemeComponents/Header";
import Footer from "../../ThemeComponents/Footer";
import Stepper from "../../ThemeComponents/ReservationPage/Stepper";
import ReservationVehicle from "../../ThemeComponents/ReservationPage/ReservationVehicle";
import ReservationOptionsPanel from "../../ThemeComponents/ReservationPage/ReservationOptionsPanel";
import ReservationInitLoader from "../../ThemeComponents/ReservationPage/ReservationInitLoader";
import ReservationStepNotice from "../../ThemeComponents/ReservationPage/ReservationStepNotice";
import {NavLink, useLocation, useNavigate, useParams} from "react-router-dom";
import {detailVehicleAPI} from "../../network/publicCalls/GETVehicleDetail";
import {DISABLED_CACHE} from "../../config/network";
import {appendPublicWrapperBaseToSearch} from "../../config/publicWrapperBase";
import demoLogo from "../../assets/images/demo.png";
import Devider from "../../ThemeComponents/Devider";
import PriceVatToggle from "../../ThemeComponents/PriceVatToggle";
import {getPriceVatPreference, setPriceVatPreference} from "../../config/priceVatPreference";
import {forceScrollToTop} from "../../config/scrollToTop";
import {sendEvent} from "../../config/analytics";

const DEMO_LOCATION_ID = '875'

const ReservationOptions = () => {
    const {vehicleId, locationId} = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const isDemoReservationLocation = String(locationId) === DEMO_LOCATION_ID

    const queryParams = new URLSearchParams(location.search)
    const rentalPeriod = {
        from: queryParams.get('from') || '',
        to: queryParams.get('to') || ''
    }
    const expectedKm = queryParams.get('expectedKm') || ''
    const defaultProductRentalDays = React.useMemo(() => {
        if (!rentalPeriod.from || !rentalPeriod.to) return 1
        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1
        const diffDays = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
        return Math.max(1, diffDays)
    }, [rentalPeriod.from, rentalPeriod.to])

    const [vehicleInfo, setVehicleInfo] = useState(null)
    const [selectedOptionIds, setSelectedOptionIds] = useState([])
    const [selectedProducts, setSelectedProducts] = useState({})
    const [optionsReady, setOptionsReady] = useState(false)
    const [showPricesInclVat, setShowPricesInclVat] = useState(() => getPriceVatPreference(false))

    useEffect(() => {
        forceScrollToTop()
    }, [location.pathname])

    useEffect(() => {
        sendEvent("reserve_options_view")
    }, [])

    useEffect(() => {
        if (vehicleId) {
            (async () => {
                const vehicleResponse = await detailVehicleAPI({vehicleId, force: DISABLED_CACHE})
                setVehicleInfo(vehicleResponse)
            })()
        }
    }, [vehicleId]);

    const productsQueryValue = Object.entries(selectedProducts || {})
        .map(([productId, productPayload]) => {
            const parsedProductId = Number(productId)
            const parsedQuantity = Number(
                typeof productPayload === "object" && productPayload !== null
                    ? productPayload.quantity
                    : productPayload
            )
            const parsedDays = Number(
                typeof productPayload === "object" && productPayload !== null
                    ? productPayload.days
                    : NaN
            )
            if (!Number.isFinite(parsedProductId) || parsedProductId <= 0) return null
            if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return null
            const normalizedQty = Math.floor(parsedQuantity)
            const normalizedDays = Number.isFinite(parsedDays) && parsedDays > 0
                ? Math.floor(parsedDays)
                : null
            if (normalizedDays && normalizedDays !== defaultProductRentalDays) {
                return `${parsedProductId}:${normalizedQty}:${normalizedDays}`
            }
            return `${parsedProductId}:${normalizedQty}`
        })
        .filter(Boolean)
        .join(",")

    const confirmSearch = useMemo(() => {
        return appendPublicWrapperBaseToSearch(new URLSearchParams({
            from: rentalPeriod.from || "",
            to: rentalPeriod.to || "",
            expectedKm: expectedKm ?? "",
            options: selectedOptionIds.join(","),
            products: productsQueryValue
        }), location.search).toString()
    }, [rentalPeriod.from, rentalPeriod.to, expectedKm, selectedOptionIds, productsQueryValue, location.search])

    useEffect(() => {
        if (!vehicleInfo) return
        const hasOptions = Array.isArray(vehicleInfo.options) && vehicleInfo.options.length > 0
        const hasProducts = Array.isArray(vehicleInfo.products) && vehicleInfo.products.length > 0
        if (hasOptions || hasProducts) return

        navigate({
            pathname: `/reservation-confirm/${vehicleId}/${locationId}`,
            search: appendPublicWrapperBaseToSearch(new URLSearchParams({
                from: rentalPeriod.from || "",
                to: rentalPeriod.to || "",
                expectedKm: expectedKm ?? "",
                options: "",
                products: ""
            }), location.search).toString()
        }, {replace: true})
    }, [vehicleInfo, navigate, vehicleId, locationId, rentalPeriod.from, rentalPeriod.to, expectedKm, location.search])

    const handleOptionsChange = useCallback((payload) => {
        setSelectedOptionIds(Array.isArray(payload?.options) ? payload.options : [])
        setSelectedProducts(payload?.products || {})
        setOptionsReady(true)
    }, [])

    const confirmHref = useMemo(
        () => `/reservation-confirm/${vehicleId}/${locationId}?${confirmSearch}`,
        [vehicleId, locationId, confirmSearch]
    )
    const handlePriceVatPreferenceChange = (nextShowPricesInclVat) => {
        setShowPricesInclVat(nextShowPricesInclVat)
        setPriceVatPreference(nextShowPricesInclVat)
    }

    return (
        <>
            <Header/>
            <Devider />

            <div className="container mt-5 kav-reservation-top">
                {
                    isDemoReservationLocation ? (
                        <div className="kav-reservation-top__demoLogoWrap" aria-hidden="true">
                            <img src={demoLogo} alt="" className="kav-reservation-top__demoLogo"/>
                        </div>
                    ) : null
                }
                <div className="row">
                    <div className="col-md-4 front-reservation-promo">
                        <h1>Reserveren</h1>
                    </div>
                    <div className="col-md-4">
                        <Stepper steps={['Huurperiode', 'Opties', 'Je gegevens']} current={1}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <ReservationStepNotice step="Stap 2">
                            Kies je accessoires
                        </ReservationStepNotice>
                    </div>
                </div>
                {
                    isDemoReservationLocation ? (
                        <div className="row">
                            <div className="col-12">
                                <div className="kav-demo-step-banner">
                                    <span className="kav-demo-step-banner__badge">Stap 3</span>
                                    <p>Extra opties [Upsell!]</p>
                                </div>
                            </div>
                        </div>
                    ) : null
                }
            </div>

            <div className="container mt-5 mb-5">
                {vehicleInfo ? (
                    <div className="row">
                        <div className="col-12">
                            <PriceVatToggle
                                showPricesInclVat={showPricesInclVat}
                                onChange={handlePriceVatPreferenceChange}
                            />
                        </div>
                        <div className="col-md-6">
                            <ReservationOptionsPanel
                                vehicleInfo={vehicleInfo}
                                defaultProductRentalDays={defaultProductRentalDays}
                                onSelectionChange={handleOptionsChange}
                                showPricesInclVat={showPricesInclVat}
                            />
                            <div className="kav-step-actions">
                                <div className="kav-step-actions__row">
                                    <NavLink
                                        to={{
                                            pathname: `/reservation/${vehicleId}/${locationId}`,
                                            search: appendPublicWrapperBaseToSearch(new URLSearchParams({
                                                from: rentalPeriod.from || "",
                                                to: rentalPeriod.to || "",
                                                expectedKm: expectedKm ?? "",
                                            }), location.search).toString(),
                                        }}
                                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                    >
                                        Terug
                                    </NavLink>
                                    <div className="kav-step-actions__next">
                                        <a
                                            href={confirmHref}
                                            className="kav-step-actions__btn kav-step-actions__btn--primary"
                                        >
                                            Volgende
                                            <i className="bi bi-chevron-right" aria-hidden="true"></i>
                                        </a>
                                        <span className="kav-step-actions__hint">
                                            We hebben alleen nog wat gegevens nodig
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <ReservationVehicle
                                vehicleInfo={vehicleInfo}
                                rentalPeriod={rentalPeriod}
                                expectedKm={expectedKm}
                                selectedOptions={selectedOptionIds}
                                selectedProducts={selectedProducts}
                                optionsReady={optionsReady}
                                showDeposit={true}
                                showPricesInclVat={showPricesInclVat}
                            />
                        </div>
                    </div>
                ) : (
                    <ReservationInitLoader />
                )}
            </div>

            <Footer/>
        </>
    )
}

export default ReservationOptions
